import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

// Una sola linea del tempo: apertura della pratica, note, documenti, incassi, spese,
// comunicazioni e appuntamenti. Gli appuntamenti futuri stanno in cima.
const ETICHETTE = {
  apertura: { l: "Apertura", c: "text-[#4A4A48] bg-[#ECEAE3]" },
  nota: { l: "Nota", c: "text-[#4A4A48] bg-[#ECEAE3]" },
  documento: { l: "Documento", c: "text-[#4A4A48] bg-[#ECEAE3]" },
  incasso: { l: "Incasso", c: "text-[#1B3A22] bg-[#D8E6D2]" },
  spesa: { l: "Spesa", c: "text-[#8A2E2E] bg-[#F6E3E3]" },
  email: { l: "Email", c: "text-[#4A4A48] bg-[#ECEAE3]" },
  whatsapp: { l: "WhatsApp", c: "text-[#1B3A22] bg-[#D8E6D2]" },
  appuntamento: { l: "Appuntamento", c: "text-[#1B2A4B] bg-[#E7D9B2]" },
  altro: { l: "Evento", c: "text-[#4A4A48] bg-[#ECEAE3]" },
};

const genere = (h) => {
  const t = `${h.tipo || ""} ${h.testo || ""}`.toLowerCase();
  if (t.includes("pratica creata") || t.includes("apertura")) return "apertura";
  if (t.includes("incasso") || t.includes("pagamento")) return "incasso";
  if (t.includes("spesa") || t.includes("costo")) return "spesa";
  if (t.includes("whatsapp")) return "whatsapp";
  if (t.includes("email") || t.includes("bozza")) return "email";
  if (t.includes("ricevuta") || t.includes("documento") || t.includes("generat") || t.includes("fattura")) return "documento";
  if (t.includes("evento") || t.includes("promemoria") || t.includes("appuntamento")) return "appuntamento";
  if ((h.tipo || "") === "nota") return "nota";
  return "altro";
};

const quando = (s) => new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

// Il diario racconta i fatti della pratica. Le registrazioni, le modifiche e le cancellazioni
// restano nello Storico: un movimento cancellato non compare qui.
const TIPI_FATTO = new Set(["creazione", "nota", "documento", "sollecito", "evento",
  "scadenza", "tipo", "trasformazione", "migrazione", "ripristino", "step", "stato"]);
const RIGA_DI_REGISTRO = /sull'incasso|con ricevuta di pagamento|con fattura n|registrato (incasso|pagamento)|rimosso (incasso|pagamento)|step aggiornati/i;

export default function PraticaDiario({ pratica, onChanged, apri, setApri }) {
  const { can } = useAuth();
  const [nota, setNota] = useState("");
  const apriNota = apri === "nota";
  const apriEvento = apri === "evento";
  const chiudi = () => setApri?.(null);
  const [ev, setEv] = useState({ titolo: "", data: "", descrizione: "", avvisa: 7 });
  const [busy, setBusy] = useState(false);

  const { data: denaro } = useQuery({
    queryKey: ["denaro", pratica.id],
    queryFn: async () => (await api.get(`/pratiche/${pratica.id}/denaro`)).data,
    enabled: can("vedi_dati_economici"),
  });

  const righe = useMemo(() => {
    const out = [];
    for (const n of pratica.note || []) {
      out.push({ id: `n-${n.id}`, t: n.timestamp, g: "nota", testo: n.testo, autore: n.autore, notaId: n.id });
    }
    for (const h of pratica.storico || []) {
      const tipo = (h.tipo || "").toLowerCase();
      if (!TIPI_FATTO.has(tipo)) continue;
      if (RIGA_DI_REGISTRO.test(h.testo || "")) continue;
      out.push({ id: `s-${h.timestamp}-${h.testo?.slice(0, 12)}`, t: h.timestamp, g: genere(h), testo: h.testo });
    }
    for (const m of denaro?.movimenti || []) {
      const incasso = m.genere === "incasso";
      const dettagli = [incasso ? m.mezzo : m.mezzo && `a ${m.mezzo}`,
        incasso ? null : (m.pagato ? "pagata" : "da pagare"),
        m.documento ? `${m.documento.etichetta} n. ${m.documento.numero_completo}` : null].filter(Boolean);
      out.push({
        id: `m-${m.id}`, t: `${m.data}T12:00`, g: incasso ? "incasso" : "spesa",
        testo: `${incasso ? "Incasso" : "Spesa"} di ${eur(Math.abs(m.importo))}`
          + (m.causale && m.causale !== "Incasso" ? ` · ${m.causale}` : ""),
        sotto: dettagli.join(" · "),
      });
    }
    out.sort((a, b) => String(b.t).localeCompare(String(a.t)));
    const futuri = (pratica.eventi || [])
      .filter((e) => new Date(e.data) >= new Date())
      .sort((a, b) => String(a.data).localeCompare(String(b.data)))
      .map((e) => ({ id: `e-${e.id}`, t: e.data, g: "appuntamento", testo: e.titolo, link: e.htmlLink, futuro: true }));
    return [...futuri, ...out];
  }, [pratica, denaro]);

  const salvaNota = async () => {
    if (!nota.trim()) return;
    setBusy(true);
    try {
      await api.post(`/pratiche/${pratica.id}/note`, { testo: nota });
      setNota(""); chiudi(); toast.success("Nota salvata nel diario"); onChanged?.();
    } catch { toast.error("Nota non salvata"); } finally { setBusy(false); }
  };

  const creaEvento = async () => {
    if (!ev.data) return toast.error("Scegli data e ora");
    setBusy(true);
    try {
      await api.post(`/pratiche/${pratica.id}/evento`, {
        titolo: ev.titolo || `${pratica.cliente?.nome || ""} ${pratica.cliente?.cognome || ""} — ${pratica.tipo_pratica_nome || ""}`.trim(),
        data: ev.data + ":00", descrizione: ev.descrizione, avvisa_giorni: Number(ev.avvisa) || null,
      });
      toast.success("Appuntamento creato su Google Calendar");
      chiudi(); setEv({ titolo: "", data: "", descrizione: "", avvisa: 7 }); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Appuntamento non creato"); }
    finally { setBusy(false); }
  };

  const eliminaNota = async (nid) => {
    try { await api.delete(`/pratiche/${pratica.id}/note/${nid}`); onChanged?.(); }
    catch { toast.error("Nota non rimossa"); }
  };

  const campo = "w-full mt-1 px-3 py-2 text-[15px] border border-[#C6C3BA] rounded-[4px] focus:outline-none focus:border-[#A8863F]";
  const etich = "eti";

  return (
    <div data-testid="pratica-diario">
      {apriNota && (
        <div data-testid="diario-form-nota" className="mb-[18px] rounded-[5px] border border-[#C6C3BA] bg-[#FAFAF7] p-3.5 flex flex-col gap-2.5">
          <span className="eti">Nuova nota</span>
          <textarea data-testid="nota-input" rows={2} value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Scrivi qui. Finisce nel diario con la data di oggi."
            className="text-[15px] border border-[#C6C3BA] rounded-[4px] px-3 py-2.5 focus:outline-none focus:border-[#A8863F] resize-y" />
          <div className="flex flex-wrap items-center gap-2.5">
            <button data-testid="nota-add" onClick={salvaNota} disabled={busy} className="btn btn-p px-5 py-2.5">Salva</button>
            <button data-testid="nota-annulla" onClick={() => { chiudi(); setNota(""); }} className="btn btn-o px-5 py-2.5">Annulla</button>
            <span className="text-[13px] text-[#5A5A5A]">«Evento in agenda» apre invece data, ora e promemoria.</span>
          </div>
        </div>
      )}

      {apriEvento && (
        <div data-testid="diario-form-evento" className="mb-[18px] rounded-[5px] border border-[#C6C3BA] bg-[#FAFAF7] p-3.5 grid sm:grid-cols-2 gap-2.5">
          <label className="block">
            <span className={etich}>Titolo</span>
            <input data-testid="event-titolo" value={ev.titolo} onChange={(e) => setEv({ ...ev, titolo: e.target.value })}
              placeholder="es. appuntamento in studio" className={campo} />
          </label>
          <label className="block">
            <span className={etich}>Data e ora</span>
            <input data-testid="event-data" type="datetime-local" value={ev.data}
              onChange={(e) => setEv({ ...ev, data: e.target.value })} className={campo} />
          </label>
          <label className="block">
            <span className={etich}>Promemoria quanti giorni prima</span>
            <input data-testid="event-avvisa" type="number" min="0" value={ev.avvisa}
              onChange={(e) => setEv({ ...ev, avvisa: e.target.value })} className={campo} />
          </label>
          <label className="block">
            <span className={etich}>Note</span>
            <input data-testid="event-descrizione" value={ev.descrizione}
              onChange={(e) => setEv({ ...ev, descrizione: e.target.value })} className={campo} />
          </label>
          <div className="sm:col-span-2 flex justify-end">
            <button data-testid="event-create" onClick={creaEvento} disabled={busy}
              className="btn btn-p btn-piccolo"><Plus className="w-4 h-4" /> Crea appuntamento</button>
          </div>
        </div>
      )}

      <ol className="relative" data-testid="diario-righe">
        {righe.length === 0 && <li className="text-sm text-[#4a5568]">Ancora niente nel diario.</li>}
        {righe.map((r) => {
          const e = ETICHETTE[r.g] || ETICHETTE.altro;
          return (
            <li key={r.id} data-testid={`diario-riga-${r.g}`}
              className="group flex flex-wrap sm:flex-nowrap gap-y-1 gap-x-3.5 py-3 border-b border-[#F0EEE8] last:border-0">
              <span className="w-[104px] shrink-0 text-[13px] font-bold text-[#1B2A4B]">{quando(r.t)}</span>
              <span className={`tag ${e.c}`}>{e.l}</span>
              <div className="min-w-0 flex-grow">
                <p className="text-[14px] whitespace-pre-line break-words">{r.testo}</p>
                {(r.autore || r.futuro || r.link || r.sotto) && (
                  <p className="text-[13px] text-[#5A5A5A]">
                    {r.sotto ? r.sotto : ""}{r.sotto && r.autore ? " · " : ""}{r.autore ? r.autore : ""}{r.futuro ? (r.autore ? " · " : "") + "in agenda" : ""}
                    {r.link ? <> · <a href={r.link} target="_blank" rel="noreferrer" className="font-semibold">apri in calendario</a></> : null}
                  </p>
                )}
              </div>
              {r.notaId && (
                <button data-testid={`diario-nota-remove-${r.notaId}`} onClick={() => eliminaNota(r.notaId)}
                  className="text-[#9b2c2c]/50 hover:text-[#9b2c2c] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity self-start">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ol>
      <div className="nota-tenue mt-3">
        Qui stanno i fatti della pratica. Le registrazioni, le correzioni e le cancellazioni restano
        nello Storico, in fondo alla sezione «Altro».
      </div>
    </div>
  );
}
